// OrderSteps.tsx
import { useCallback, useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getPublicInfo, useWindowSize } from "../../utils";
import { Collapse, IconButton, Typography, Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SuspenseWrapper from "./SuspenseWrapper";

import type { Step as StepType } from "utils/types/Info.types";

const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.3 } } };
const itemVariants = { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, x: 0 } };

interface Props { loading?: boolean; }

export const OrderSteps: React.FC<Props> = ({ loading = false }) => {
  const { isMobile } = useWindowSize();
  const [steps, setSteps] = useState<StepType[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(true);
  const [expanded, setExpanded] = useState<number[]>([]);
  const controls = useAnimation();
  const { ref: stepsRef, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

const fetchSteps = useCallback(async () => {
  setLoadingSteps(true);
  

  try {
    const res = await getPublicInfo<StepType>(["steps"], {
      page: 1, limit: 50, sortBy: "order", order: "asc",
    });


    const d: any = res?.data ?? null;
    let list: StepType[] = [];

    // 1) { items, meta }
    if (d && typeof d === "object" && Array.isArray(d.items)) {
      list = d.items as StepType[];
    }
    // 2) tuple [items, meta]
    else if (Array.isArray(d) && Array.isArray(d[0])) {
      list = d[0] as StepType[];
    }
    // 3) plain array
    else if (Array.isArray(d)) {
      list = d as StepType[];
    }
    // 4) legacy { id -> item }  OU  { 0: items, 1: meta }
    else if (d && typeof d === "object") {
      const values = Object.values(d);
      // ✅ cas spécial: {0: items[], 1: meta{}}
      if (values.length === 2 && Array.isArray(values[0]) && isPlainObject(values[1])) {
        list = values[0] as StepType[];
      } else {
        // map classique {id: Step}
        list = values as StepType[];
      }
    } else {
      console.warn("%c[OrderSteps] ⚠️ Unrecognized format:", "color:#ff5722;font-weight:bold;", d);
    }

    if (Array.isArray(list) && list.every((x) => x && typeof x === "object")) {
      list = [...list].sort((a, b) =>
        String(a.step || "").localeCompare(String(b.step || ""))
      );
    } else {
      list = Array.isArray(list) ? list.filter((x: any) => x && typeof x === "object" && "step" in x) : [];
    }

    setSteps(list);
  } catch (e) {
    setSteps([]);
  } finally {
    setLoadingSteps(false);
  }
}, []);

function isPlainObject(v: any): v is Record<string, any> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}


  useEffect(() => { fetchSteps(); }, [fetchSteps]);
  useEffect(() => { if (inView) controls.start("visible"); }, [controls, inView]);

  const handleExpandClick = (index: number) => {
    setExpanded((prev) => prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]);
  };

  return (
    <motion.div
      ref={stepsRef}
      className="flex w-full items-start justify-between flex-col"
      variants={containerVariants}
      initial="hidden"
      animate={controls}
    >
      {steps.map((step, index) => (
        <motion.div
          key={step.id ?? `${index}-${step.step}`}
          className={`flex gap-4 items-center justify-start ${isMobile ? "p-4" : "p-8"} flex-row w-full`}
          variants={itemVariants}
        >
          <SuspenseWrapper
            loading={loadingSteps}
            skeletonProps={{ variant: "circular", width: "50px", height: "50px", animation: "wave" }}
          >
            <div className="w-16 flex gap-4 items-center justify-center">
              <div className={`rounded-full ${isMobile ? "w-10 h-10" : "w-12 h-12"} flex items-center justify-center bg-primary text-white text-2xl`}>
                {index + 1}
              </div>
            </div>
          </SuspenseWrapper>

          <SuspenseWrapper
            loading={loadingSteps}
            skeletonProps={{ variant: "text", width: "100%", height: "50px", animation: "wave" }}
          >
            <div className={`flex flex-col ${isMobile ? "w-4/5" : "w-95/100"} items-center justify-center`}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                sx={{ borderBottom: 1, borderColor: "divider", cursor: "pointer" }}
                onClick={() => handleExpandClick(index)}
              >
                {/* ⚑ Force la couleur pour éviter un héritage indésirable */}
                <Typography variant={isMobile ? "body1" : "h6"} sx={{ color: "text.primary" }}>
                  {step.step}
                </Typography>
                <IconButton aria-expanded={expanded.includes(index)} aria-label="show more">
                  {expanded.includes(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expanded.includes(index)} timeout="auto" unmountOnExit sx={{ width: "100%" }}>
                <Typography sx={{ width: "100%", p: 2, whiteSpace: "normal", textAlign: "left", color: "text.primary" }}>
                  {step.description}
                </Typography>
              </Collapse>
            </div>
          </SuspenseWrapper>
        </motion.div>
      ))}
    </motion.div>
  );
};
